/**
 * Exam Scorer — calculates scores, per-domain breakdowns, and pass/fail.
 */

import { DOMAINS, PASSING_SCORE } from '../../../shared/constants/domains';

/**
 * Score a completed exam.
 *
 * @param {Array} questions - Question objects
 * @param {Object} answers - { questionId: answer }
 * @param {Object} timePerQuestion - { questionId: seconds }
 * @returns {Object} Detailed score breakdown
 */
export function scoreExam(questions, answers, timePerQuestion = {}) {
  let totalCorrect = 0;
  const perDomain = {};
  const perQuestion = {};

  // Initialize domain buckets
  for (const domain of DOMAINS) {
    perDomain[domain.id] = { correct: 0, total: 0, percentage: 0 };
  }

  for (const question of questions) {
    const answer = answers[question.id];
    const isCorrect = checkAnswer(question, answer);

    if (isCorrect) totalCorrect++;

    // Per-domain
    if (perDomain[question.domain]) {
      perDomain[question.domain].total++;
      if (isCorrect) perDomain[question.domain].correct++;
    }

    // Per-question
    perQuestion[question.id] = {
      correct: isCorrect,
      userAnswer: answer,
      timeSpent: timePerQuestion[question.id] || 0,
    };
  }

  // Calculate domain percentages
  for (const domain of DOMAINS) {
    const d = perDomain[domain.id];
    d.percentage = d.total > 0 ? Math.round((d.correct / d.total) * 100) : 0;
  }

  const totalQuestions = questions.length;
  const percentage = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;

  // Estimate scaled score (825 passing out of 1000)
  // Simple linear mapping: 0% = 300, 100% = 1000
  const estimatedScaled = Math.round(300 + (percentage / 100) * 700);

  return {
    total_correct: totalCorrect,
    total_questions: totalQuestions,
    percentage,
    estimated_scaled: estimatedScaled,
    passed: estimatedScaled >= PASSING_SCORE,
    per_domain: perDomain,
    per_question: perQuestion,
  };
}

/**
 * Check if an answer is correct for a given question.
 */
export function checkAnswer(question, answer) {
  if (answer === undefined || answer === null) return false;

  switch (question.type) {
    case 'single_choice':
      return answer === question.correct_answer;

    case 'multi_choice': {
      // All-or-nothing: must select exact correct set
      if (!Array.isArray(answer)) return false;
      const correct = Array.isArray(question.correct_answer)
        ? question.correct_answer
        : [question.correct_answer];
      if (answer.length !== correct.length) return false;
      return correct.every(c => answer.includes(c)) && answer.every(a => correct.includes(a));
    }

    case 'drag_drop': {
      // Score by number of correct placements
      if (!answer || typeof answer !== 'object') return false;
      const targets = question.drop_targets || [];
      let correctCount = 0;
      for (const target of targets) {
        const accepts = Array.isArray(target.accepts) ? target.accepts : [target.accepts];
        if (accepts.includes(answer[target.id])) correctCount++;
      }
      return correctCount === targets.length;
    }

    default:
      return answer === question.correct_answer;
  }
}
